import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, FileDown, Sparkles, Heart, Users, Clock,
  BookOpen, MapPin, Phone, Mail,
} from "lucide-react";
import { IMAGES } from "@/data/content";
import { API, createYesteryearBooking, fetchYesteryearOptions } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const TOPICS = [
  { icon: "star",   title: "Great Melbourne identities",
    body: "From Sir John Monash to Dame Nellie Melba, from Squizzy Taylor to Ron Barassi — the names that made a city." },
  { icon: "trophy", title: "Landmarks &amp; icons",
    body: "The Cup, the trams that rattle on, the clocks at Flinders Street, the Windsor's afternoon tea." },
  { icon: "home",   title: "Australia of yesteryear",
    body: "Post-war migration, the milk bar and corner pub, Sunday roasts, country dances, the songs everyone knew." },
  { icon: "medal",  title: "Melbourne at war",
    body: "Home-front stories, the Anzac spirit, letters home, the streets that farewelled a generation." },
];

const OPTION_DETAILS = {
  "1h": { badge: "Most popular",   highlight: false },
  "2h": { badge: "Extended visit", highlight: true },
};

export default function YesteryearTalks() {
  const [options, setOptions] = useState({});

  useEffect(() => {
    fetchYesteryearOptions()
      .then((d) => setOptions(d?.options || {}))
      .catch(() => setOptions({}));
  }, []);

  return (
    <div data-testid="yesteryear-page" className="bg-[#FDFBF7]">
      {/* HERO — sepia, warm, dignified */}
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img
          src={IMAGES.collinsDome}
          alt="Old Melbourne architecture"
          className="absolute inset-0 w-full h-full object-cover sepia-soft"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1817]/60 via-[#1A1817]/40 to-[#1A1817]/80" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-14">
          <div className="stamp bg-[#FDFBF7]/90 w-fit" data-testid="yy-eyebrow">
            Martin's Marvellous Melbourne Talking Series
          </div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4 leading-tight" data-testid="yy-title">
            Yesteryear with Martin.
          </h1>
          <p className="text-[#F5EFEB] mt-4 max-w-2xl text-lg">
            A warm 60-minute presentation for retirement villages and aged-care residents —
            bringing to life the Melbourne, the identities and the great Australia they grew up in.
          </p>
        </div>
      </section>

      {/* INTRO / WHAT TO EXPECT */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-5 gap-10 items-start">
        <div className="md:col-span-3">
          <div className="stamp">A visit that leaves them talking for weeks</div>
          <h2 className="serif text-4xl text-[#6E2638] mt-4 leading-tight">
            An hour of stories your residents will never forget.
          </h2>
          <div className="mt-6 text-[#1A1817]/90 leading-relaxed text-lg space-y-4">
            <p>
              Martin comes to your venue with a lifetime of Melbourne stories — the identities they
              remember, the streets they walked, the songs they sang, and the great Australia of
              their youth.
            </p>
            <p>
              This is not a lecture. It's a warm, engaging, image-rich conversation, paced for
              seniors, with plenty of room for their own memories to flow. It's history &mdash; but
              it's <em>their</em> history.
            </p>
            <p className="serif text-[#6E2638] text-xl italic">
              "The best Tuesday afternoon our residents have had all year."
            </p>
          </div>
        </div>

        <aside className="md:col-span-2 card-warm rounded-2xl p-7">
          <Heart className="text-[#6E2638]" size={28} />
          <div className="serif text-2xl text-[#6E2638] mt-2">Every visit includes</div>
          <ul className="mt-4 space-y-3 text-[#1A1817]/85">
            {[
              "Martin comes to your venue — no bus required",
              "Seated presentation with photos and visual aids",
              "Gentle Q&A — your residents share their own stories",
              "Warm, dignified delivery tuned to your audience",
              "Perfect for weekly programs, Anzac & Australia Day",
              "Metro Melbourne travel included",
            ].map((it) => (
              <li key={it} className="flex gap-3">
                <CheckCircle2 size={18} className="text-[#1E4637] shrink-0 mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </aside>
      </section>

      {/* TOPIC CARDS */}
      <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-16" data-testid="yy-topics">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <OrnamentalDivider label="Stories &amp; topics" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {TOPICS.map((t) => (
              <div key={t.title} className="card-warm rounded-2xl p-6 flex flex-col" data-testid={`yy-topic-${t.title.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <BookOpen className="text-[#B08B2E]" size={26} />
                <h3
                  className="serif text-xl text-[#6E2638] mt-3"
                  dangerouslySetInnerHTML={{ __html: t.title }}
                />
                <p className="mt-2 text-sm text-[#1A1817]/85 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING & BOOKING */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="yy-pricing">
        <div className="max-w-2xl">
          <div className="stamp" data-testid="yy-free-stamp">Free for your residents</div>
          <h2 className="serif text-4xl text-[#6E2638] mt-4">Choose your session.</h2>
          <p className="mt-4 text-[#1A1817]/85 text-lg">
            Request a free talk online, or send an enquiry and Martin will call you back within 24 hours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {Object.entries(options).map(([id, opt]) => (
            <PricingCard key={id} optionId={id} option={opt} />
          ))}
          {Object.keys(options).length === 0 && (
            <div className="col-span-full text-center py-12">
              <Loader2 className="animate-spin mx-auto text-[#6E2638]" />
            </div>
          )}
        </div>

        {/* Enquiry alternative */}
        <div className="mt-14 rounded-2xl bg-[#FDF7E7] border-2 border-dashed border-[#B08B2E] p-8" data-testid="yy-enquiry-block">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <h3 className="serif text-2xl text-[#6E2638]">Not quite ready to book?</h3>
              <p className="mt-2 text-[#1A1817]/85">
                Prefer to chat first, discuss custom themes (Anzac Day, Australia Day, migrant stories),
                or plan a series of visits? Send Martin an enquiry.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <EnquiryDialog />
            </div>
          </div>
        </div>
      </section>

      {/* BROCHURE + CONTACT STRIP */}
      <section className="bg-[#1E4637] text-[#FDFBF7]" data-testid="yy-contact-strip">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-3 gap-8">
          <div>
            <FileDown size={28} className="text-[#E7C766]" />
            <h3 className="serif text-2xl mt-3">Hard-copy brochure</h3>
            <p className="text-sm text-[#F5EFEB]/85 mt-2">
              Printable single-page brochure — perfect to drop off at your local aged-care facilities.
            </p>
            <a
              href={`${API}/brochure/yesteryear`}
              className="btn-outline-gold mt-5 inline-flex"
              data-testid="yy-brochure-download"
            >
              <FileDown size={16} /> Download brochure
            </a>
          </div>
          <div>
            <Phone size={28} className="text-[#E7C766]" />
            <h3 className="serif text-2xl mt-3">Call Martin directly</h3>
            <p className="text-sm text-[#F5EFEB]/85 mt-2">
              Happy to take bookings by phone or SMS — old-fashioned way, still the best way.
            </p>
            <a href="tel:+61422007703" className="btn-outline-gold mt-5 inline-flex" data-testid="yy-phone">
              <Phone size={16} /> 0422 007 703
            </a>
          </div>
          <div>
            <Mail size={28} className="text-[#E7C766]" />
            <h3 className="serif text-2xl mt-3">Email Martin</h3>
            <p className="text-sm text-[#F5EFEB]/85 mt-2">
              For custom themes or multi-visit arrangements across a group.
            </p>
            <a href="mailto:Mmmwt35@gmail.com" className="btn-outline-gold mt-5 inline-flex" data-testid="yy-email">
              <Mail size={16} /> Mmmwt35@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function PricingCard({ optionId, option }) {
  const details = OPTION_DETAILS[optionId] || {};
  const border = details.highlight ? "border-[#6E2638] shadow-xl" : "border-[#E5D9C5]";
  return (
    <div
      className={`rounded-2xl bg-[#FDFBF7] border-2 ${border} p-8 flex flex-col relative`}
      data-testid={`yy-option-${optionId}`}
    >
      {details.badge && (
        <span className="absolute -top-3 left-6 bg-[#6E2638] text-[#FDFBF7] text-[10px] uppercase tracking-[0.24em] px-3 py-1 rounded-full">
          {details.badge}
        </span>
      )}
      <div className="flex items-center gap-2 text-[#1E4637]">
        <Clock size={18} />
        <span className="uppercase tracking-[0.2em] text-xs">{option.hours} hour{option.hours > 1 ? "s" : ""}</span>
      </div>
      <h3 className="serif text-3xl text-[#6E2638] mt-3">{option.label}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="serif text-5xl text-[#6E2638]" data-testid={`yy-free-${optionId}`}>Free</span>
        <span className="text-sm text-[#1A1817]/60">no cost to your venue</span>
      </div>
      <ul className="mt-6 space-y-2 text-sm text-[#1A1817]/85 flex-1">
        <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Delivered at your venue</li>
        <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Photos, visual aids &amp; Q&amp;A</li>
        <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Up to 200 residents</li>
        <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#1E4637] shrink-0 mt-0.5" /> Metro Melbourne travel included</li>
      </ul>
      <BookingDialog optionId={optionId} option={option} />
    </div>
  );
}

function BookingDialog({ optionId, option }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    venue_name: "", contact_name: "", contact_email: "", contact_phone: "",
    preferred_date: "", audience_size: "", notes: "",
  });
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createYesteryearBooking({
        option_id: optionId,
        venue_name: form.venue_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        preferred_date: form.preferred_date || null,
        audience_size: form.audience_size ? Number(form.audience_size) : null,
        notes: form.notes || null,
      });
      setDone(true);
      toast.success("Request sent — Martin will call within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDone(false); }}>
      <DialogTrigger asChild>
        <button className="btn-primary mt-6 w-full justify-center" data-testid={`yy-book-btn-${optionId}`}>
          Request a free talk
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="serif text-[#6E2638] text-2xl">Request a free {option.label}</DialogTitle>
          <DialogDescription>
            Fill in the details and Martin will call within 24 hours to lock in the date.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-6" data-testid={`yy-success-${optionId}`}>
            <CheckCircle2 size={40} className="mx-auto text-[#1E4637]" />
            <div className="serif text-2xl text-[#6E2638] mt-3">Request received</div>
            <p className="mt-2 text-[#1A1817]/80">
              A confirmation is on its way to {form.contact_email}. Martin will call you within 24 hours.
            </p>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="yy-venue">Venue / village name</Label>
            <Input id="yy-venue" data-testid="yy-input-venue" required
              value={form.venue_name} onChange={(e) => update("venue_name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="yy-name">Your name</Label>
              <Input id="yy-name" data-testid="yy-input-name" required
                value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="yy-email">Email</Label>
              <Input id="yy-email" type="email" data-testid="yy-input-email" required
                value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="yy-phone">Phone (optional)</Label>
              <Input id="yy-phone" data-testid="yy-input-phone"
                value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="yy-size">Audience size (approx)</Label>
              <Input id="yy-size" type="number" min={1} max={500} data-testid="yy-input-size"
                value={form.audience_size} onChange={(e) => update("audience_size", e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="yy-date">Preferred date (optional)</Label>
            <Input id="yy-date" type="date" data-testid="yy-input-date"
              value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="yy-notes">Any special themes or notes?</Label>
            <Textarea id="yy-notes" data-testid="yy-input-notes" rows={3}
              placeholder="Anzac Day tribute · migrant stories · specific decades…"
              value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center"
                  data-testid={`yy-submit-${optionId}`}>
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
                        : <>Send my request &rarr;</>}
          </button>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EnquiryDialog() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    tour_type: "yesteryear",
    contact_name: "",
    organisation: "",
    email: "",
    phone: "",
    group_size: 20,
    preferred_date: "",
    message: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await (await import("@/lib/api")).api.post("/enquiries", form);
      toast.success("Enquiry sent — Martin will be in touch within 24 hours.");
      setOpen(false);
      setForm({ ...form, contact_name: "", organisation: "", email: "", phone: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send enquiry. Please try email instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-outline-gold" data-testid="yy-enquiry-btn">
          Send an enquiry instead
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="serif text-[#6E2638] text-2xl">Send Martin an enquiry</DialogTitle>
          <DialogDescription>
            Martin will call within 24 hours to talk through your visit.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="yy-eq-org">Venue / village name</Label>
            <Input id="yy-eq-org" data-testid="yy-eq-input-org" required
              value={form.organisation} onChange={(e) => update("organisation", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="yy-eq-name">Your name</Label>
              <Input id="yy-eq-name" data-testid="yy-eq-input-name" required
                value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="yy-eq-email">Email</Label>
              <Input id="yy-eq-email" type="email" data-testid="yy-eq-input-email" required
                value={form.email} onChange={(e) => update("email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="yy-eq-phone">Phone</Label>
              <Input id="yy-eq-phone" data-testid="yy-eq-input-phone"
                value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="yy-eq-size">Audience size (approx)</Label>
              <Input id="yy-eq-size" type="number" min={1} data-testid="yy-eq-input-size"
                value={form.group_size} onChange={(e) => update("group_size", Number(e.target.value) || 1)} />
            </div>
          </div>
          <div>
            <Label htmlFor="yy-eq-date">Preferred date (optional)</Label>
            <Input id="yy-eq-date" type="date" data-testid="yy-eq-input-date"
              value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="yy-eq-msg">Message</Label>
            <Textarea id="yy-eq-msg" data-testid="yy-eq-input-msg" rows={3}
              placeholder="Tell Martin about your residents and any themes you'd like."
              value={form.message} onChange={(e) => update("message", e.target.value)} />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center"
                  data-testid="yy-eq-submit">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
                        : <>Send enquiry</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
